import { ReactNode, useMemo, useState } from "react";
import useDatasets from "datasets/hooks/useDatasets";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { AlertCircle, Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFavorites } from "~/hooks/useFavorites";
import { useSort, type SortOption } from "~/hooks/useSort";
import useUserPermission from "~/hooks/useUserPermission";
import { useViewMode } from "~/hooks/useViewMode";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { SortMenu } from "~/pages/manifold-nextgen/components/SortMenu";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { resourceIcon } from "~/pages/manifold-nextgen/components/resourceIcon";
import usePersonalDataset from "~/pages/manifold-nextgen/hooks/usePersonalDataset";
import { CreateDatasetModal } from "./CreateDatasetModal";
import { DatasetCard } from "./DatasetCard";
import { PersonalDatasetCard } from "./PersonalDatasetCard";

const SKELETON_COUNT = 6;

const DATASET_SORT_OPTIONS: SortOption<DatasetListItem>[] = [
  {
    key: "updated_at",
    label: "Last updated",
    kind: "date",
    accessor: (d) => Date.parse(d.updated_at),
    defaultDirection: "desc",
  },
  {
    key: "created_at",
    label: "Date created",
    kind: "date",
    accessor: (d) => Date.parse(d.created_at),
    defaultDirection: "desc",
  },
  {
    key: "title",
    label: "Name",
    kind: "alpha",
    accessor: (d) => d.title,
    defaultDirection: "asc",
  },
];

const LoadingGrid = () => {
  return (
    <div
      role="status"
      aria-label="Loading datasets"
      className="grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <Card key={i} className="h-52 gap-0 overflow-hidden p-0">
          <Skeleton className="h-10 w-full rounded-none" />
          <div className="flex flex-col gap-2 px-5 py-4">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </Card>
      ))}
    </div>
  );
};

const ErrorState = () => {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
    >
      <AlertCircle className="size-8" aria-hidden="true" />
      <p className="text-sm font-medium">Unable to load datasets</p>
    </div>
  );
};

export const Datasets = () => {
  useDocumentTitle("Datasets");
  const hasCreatePermission = useUserPermission("create:catalog-dataset");
  const { data: datasets, isLoading, error } = useDatasets();
  const { data: personalDataset, isLoading: isPersonalDatasetLoading } = usePersonalDataset();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  const isEmpty = !isLoading && !error && datasets?.length === 0;
  const showNewDatasetButton = hasCreatePermission && !error && !isEmpty;

  const filtered = useMemo(() => {
    if (!datasets) {
      return [];
    }
    const query = search.trim().toLowerCase();
    if (!query) {
      return datasets;
    }
    return datasets.filter(
      (d) =>
        d.title.toLowerCase().includes(query) || (d.description ?? "").toLowerCase().includes(query)
    );
  }, [datasets, search]);

  // The catalog API can sort server-side, but we sort the fetched page on the
  // client so all nextgen lists share one control and persistence. The server
  // order is just the initial state useSort overrides. Revisit if datasets
  // outgrow the single-page fetch in useDatasets.
  const { sortKey, direction, activeOption, setSortKey, toggleDirection, sorted } = useSort(
    "nextgen-dataset-sort",
    DATASET_SORT_OPTIONS,
    filtered
  );

  const { isFavorite, toggleFavorite, favorites, others } = useFavorites(
    "dataset-favorites",
    sorted,
    (d) => d.id
  );

  // While searching, the Favorites/All split is noise — show a single flat list.
  const showGroups = !search.trim() && favorites.length > 0;

  const renderDatasets = (items: DatasetListItem[]) =>
    viewMode === "card" ? (
      <ul className="grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
        {items.map((dataset) => (
          <li key={dataset.id} className="flex h-full flex-col">
            <DatasetCard
              dataset={dataset}
              view="card"
              favorite={isFavorite(dataset.id)}
              onToggleFavorite={() => toggleFavorite(dataset.id)}
            />
          </li>
        ))}
      </ul>
    ) : (
      <DividedList
        items={items}
        renderItem={(dataset) => (
          <DatasetCard
            dataset={dataset}
            view="list"
            favorite={isFavorite(dataset.id)}
            onToggleFavorite={() => toggleFavorite(dataset.id)}
          />
        )}
      />
    );

  const headerActions = useMemo<[ReactNode?, ReactNode?]>(
    () =>
      showNewDatasetButton
        ? [
            <CreateDatasetModal
              key="new-dataset"
              trigger={
                <Button size="sm" className="text-xs" data-testid="new-dataset">
                  <Plus aria-hidden="true" />
                  New Dataset
                </Button>
              }
            />,
          ]
        : [],
    [showNewDatasetButton]
  );

  useNextGenHeader({ actions: headerActions });
  usePageContext({ page: "datasets" });

  return (
    <div
      className="mx-auto flex w-full flex-col gap-6 px-4 py-4"
      style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
    >
      {!isLoading && !error && !isEmpty && (
        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-lg">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search datasets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search datasets"
              className="h-8 pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <SortMenu
              options={DATASET_SORT_OPTIONS}
              sortKey={sortKey}
              direction={direction}
              activeLabel={activeOption.label}
              onSelectKey={setSortKey}
              onToggleDirection={toggleDirection}
            />
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>
      )}

      <PersonalDatasetCard dataset={personalDataset} isLoading={isPersonalDatasetLoading} />

      {isLoading && viewMode === "list" && (
        <ListLoadingSkeleton
          aria-label="Loading datasets"
          columns={[{ width: "w-24", height: "h-5", rounded: true }, { width: "w-44" }]}
        />
      )}
      {isLoading && viewMode === "card" && <LoadingGrid />}
      {!isLoading && error && <ErrorState />}
      {isEmpty && (
        <StatusMessage
          variant="empty"
          data-testid="nextgen-dataset-list-empty"
          title="No datasets found"
          body="You don't have access to any datasets in your organization."
          icon={resourceIcon("datasets")}
          className="py-16"
          action={
            hasCreatePermission && (
              <CreateDatasetModal
                trigger={
                  <Button size="sm" className="mt-2 text-xs">
                    <Plus aria-hidden="true" />
                    New Dataset
                  </Button>
                }
              />
            )
          }
        />
      )}
      {!isLoading && !error && datasets && datasets.length > 0 && filtered.length === 0 && (
        <StatusMessage
          variant="empty"
          title="No datasets match your search"
          body={search ? "Try a different search term" : undefined}
          icon={resourceIcon("datasets")}
          className="py-16"
        />
      )}
      {!isLoading && !error && filtered.length > 0 && (
        <div data-testid="nextgen-dataset-list" className="flex flex-col gap-6">
          {showGroups ? (
            <>
              <section className="flex flex-col gap-3">
                <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Favorites
                </h2>
                {renderDatasets(favorites)}
              </section>
              {others.length > 0 && (
                <>
                  <Separator />
                  {renderDatasets(others)}
                </>
              )}
            </>
          ) : (
            renderDatasets(sorted)
          )}
        </div>
      )}
    </div>
  );
};
